/**
 * Tiny shared store between the Communications breadcrumb bar (rendered by
 * Header) and the Communications view. The bar owns the controls, the view owns
 * the data; this carries the filter and one-shot "compose" / "refresh" / "RSVP reminder" requests
 * between them without adding props to the global Header.
 */
import { useSyncExternalStore } from 'react';
import type { InboxFilter } from '../../types/Communications';

export interface InboxUiState {
  filter: InboxFilter;
  /** Bumped by the bar's Compose button; the view opens the compose dialog when it changes. */
  composeNonce: number;
  /** Bumped by the bar's Refresh button; the view reloads when it changes. */
  refreshNonce: number;
  /** Bumped by the bar's RSVP reminder button; the view opens the reminder dialog when it changes. */
  reminderNonce: number;
}

let state: InboxUiState = { filter: 'all', composeNonce: 0, refreshNonce: 0, reminderNonce: 0 };
const listeners = new Set<() => void>();

function update(patch: Partial<InboxUiState>): void {
  state = { ...state, ...patch };
  listeners.forEach(l => l());
}

export const getInboxUi = (): InboxUiState => state;

export function subscribeInboxUi(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export const setInboxFilter = (filter: InboxFilter): void => update({ filter });
export const requestCompose = (): void => update({ composeNonce: state.composeNonce + 1 });
export const requestRefresh = (): void => update({ refreshNonce: state.refreshNonce + 1 });
export const requestRsvpReminder = (): void => update({ reminderNonce: state.reminderNonce + 1 });
export const resetInboxUi = (): void => update({ filter: 'all' });

export function useInboxUi(): InboxUiState {
  return useSyncExternalStore(subscribeInboxUi, getInboxUi, getInboxUi);
}
